import { NEW_LOOK_RADIUS } from "common/NewLook/tokens.ts";
import styled from "styled-components";
import { LanguageNameSpaces, WorkspaceType } from "@shared/types";
import { CSSProperties, useMemo } from "react";
import { motion } from "motion/react";
import Text from "common/Typography/Text";
import useWorkspace from "utils/hooks/useWorkspace";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { WORKSPACE_SWITCH_ID } from "testIds";

interface WorkspaceSwitchProps {
  isShort?: boolean;
  style?: CSSProperties;
}

const WorkspaceSwitch = ({ isShort, style }: WorkspaceSwitchProps) => {
  const { workspace, onWorkspaceChange } = useWorkspace();
  const isDemo = useMemo(() => workspace === WorkspaceType.DEMO, [workspace]);
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);

  if (workspace !== WorkspaceType.USER && workspace !== WorkspaceType.DEMO)
    return null;

  const onSwitchClick = () => {
    if (workspace === WorkspaceType.DEMO) onWorkspaceChange(WorkspaceType.USER);
    else onWorkspaceChange(WorkspaceType.DEMO);
  };

  const selectedText = isDemo
    ? t("demo_mode.workspace_demo_full")
    : t("demo_mode.workspace_live_full");
  const unselectedText = isDemo
    ? t(
        isShort
          ? "demo_mode.workspace_live_short"
          : "demo_mode.workspace_live_full"
      )
    : t(
        isShort
          ? "demo_mode.workspace_demo_short"
          : "demo_mode.workspace_demo_full"
      );

  return (
    <Background
      type="button"
      data-testid={WORKSPACE_SWITCH_ID}
      role="switch"
      aria-label={t("demo_mode.workspace_demo_full")}
      aria-checked={isDemo}
      onClick={onSwitchClick}
      $isDemo={isDemo}
      $isShort={isShort}
      style={style}
      layout
      layoutRoot
      transition={{ ease: "easeInOut", duration: 0.3 }}
    >
      <WorkspaceText $isDemo={isDemo} $isShort={isShort}>
        {unselectedText}
      </WorkspaceText>
      <WorkspaceBg
        layout
        transition={{
          type: "spring",
          damping: 30,
          stiffness: 250,
        }}
        $isDemo={isDemo}
        $isShort={isShort}
      >
        {selectedText}
      </WorkspaceBg>
    </Background>
  );
};

export default WorkspaceSwitch;

const Background = styled(motion.button)<{
  $isDemo?: boolean;
  $isShort?: boolean;
}>`
  display: flex;
  border: 0;
  font: inherit;
  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.primaryBlue};
    outline-offset: 3px;
  }
  align-items: center;
  background: ${(p) => p.theme.colors.lightBlue};
  cursor: pointer;
  border-radius: 6px;
  height: 32px;
  padding: 4px;
  ${(p) => !p.$isShort && `width: 100%`};
  ${(p) =>
    p.$isDemo &&
    `
    justify-content: flex-end;
  `};
  ${(p) =>
    p.theme.isNewLook &&
    `
    border-radius: ${p.theme.radii.control}px;
    background: ${p.theme.colors.cardLightBgColor};
    box-shadow: inset 0 0 0 1px ${p.theme.colors.lightestBorderGrey};
  `}
`;
const WorkspaceBg = styled(motion.div)<{
  $isDemo?: boolean;
  $isShort?: boolean;
}>`
  width: ${(p) => (p.$isShort ? "auto" : "50%")};
  height: 26px;
  border-radius: 4px;
  padding: 4px 12px;
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  text-align: center;
  color: ${(p) => p.theme.colors.layoutBgColor};
  order: ${(p) => (p.$isDemo ? 2 : 1)};
  background: ${(p) =>
    p.$isDemo
      ? `linear-gradient(114.66deg, ${p.theme.colors.demoTagColors})`
      : p.theme.colors.primaryBlue};
  ${(p) =>
    p.theme.isNewLook &&
    `
    background: ${p.theme.colors.cardBgColor};
    color: ${p.theme.colors.titleColor};
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08),
      0 0 0 1px ${p.theme.colors.cardBorderColor};
    height: 24px;
    line-height: 16px;
    border-radius: ${NEW_LOOK_RADIUS.chip}px;
    font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
  `}
`;
const WorkspaceText = styled(Text)<{ $isDemo: boolean; $isShort?: boolean }>`
  text-align: center;
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  color: ${(p) => p.theme.colors.primaryBlue};
  order: ${(p) => (p.$isDemo ? 1 : 2)};
  line-height: 18px;
  width: auto;
  white-space: nowrap;
  margin: 0 auto;
  ${(p) =>
    p.$isShort &&
    `
      padding: 0 8px;
  `};
  ${(p) =>
    p.theme.isNewLook &&
    `
    color: ${p.theme.colors.secondaryText};
    font-weight: ${p.theme.typography.fontWeights.MEDIUM};
    transition: color 0.15s ease;
    button:hover > & {
      color: ${p.theme.colors.titleColor};
    }
  `}
`;
